import { PropsWithChildren } from "react";
import { gapClassName, textFontClassName, transitionClassName } from "./commonClassNames";
import { GapProp } from "./commonProps";
import { Icon } from "./Icon";

export type AccordionProps = GapProp & { className?: string };

export type AccordionSectionProps = {
  title: string | React.ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  className?: string;
  isBaseMargin?: boolean;
  isBaseHeight?: boolean;
  isBorder?: boolean;
  isCustomScroll?: boolean;
};

export const Accordion: React.FC<PropsWithChildren<AccordionProps>> = ({ children, gap, className }) => {
  const combinedClassName = ["flex flex-col", gapClassName({ gap }), className ?? ""].join(" ");

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <div className={combinedClassName} onClick={onClick}>
      {children}
    </div>
  );
};

export const AccordionSection: React.FC<PropsWithChildren<AccordionSectionProps>> = ({
  children,
  title,
  isOpen,
  setIsOpen,
  className,
  isBaseMargin,
  isBorder,
  isBaseHeight,
  isCustomScroll = false,
}) => {
  const containerClassName = [
    "bg-neutral-100 text-black",
    "flex flex-col w-80",
    "first:rounded-t-md last:rounded-b-md",
    className ?? "",
  ].join(" ");

  const horizontalMargin = `${isBaseMargin ? "mx-4 md:mx-6" : "mx-0"}`;

  const headerClassName = [
    "flex flex-row justify-between items-center",
    `${isBaseHeight ? "h-10" : "h-12"}`,
    textFontClassName({ font: "medium", size: "sm", uppercase: false }),
    horizontalMargin,
    "select-none",
    `${isBorder && "border-solid border shadow rounded-[5px] border-color-gray pr-3 pl-6"}`,
  ].join(" ");

  const contentClassName = [
    horizontalMargin,
    // Using max-h to make it animatable,
    // but it could also makes sense to limit content height.
    isOpen ? "max-h-112" : "max-h-0",
    isCustomScroll ? "" : "overflow-x-hidden overflow-y-hidden",
    transitionClassName({ all: true }),
  ].join(" ");

  const onClickHeader = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={containerClassName}>
      <div className={headerClassName} onClick={onClickHeader}>
        <div>{title}</div>
        <Icon name={isOpen ? "caret-up" : "caret-down"} />
      </div>
      <div className={contentClassName}>{children}</div>
    </div>
  );
};
